import { getPreviousDrawingId, hasSkippedUpcomingDrawing, inferSkippedUpcomingDraw } from './getPickerDrawings';
import { hasUsablePrizeOdds } from './hasUsablePrizeOdds';

type ResultsQueryDrawing = {
  drawingId: string;
  drawingDate?: string;
  numbers?: readonly unknown[];
  odds?: Parameters<typeof hasUsablePrizeOdds>[0];
};

type ResultsQueryObserver = {
  state: {
    dataUpdatedAt: number;
    fetchFailureCount?: number;
    data?: ResultsQueryDrawing[] | null;
  };
};

type LatestResultsQueryOptions = {
  params: { limit: number };
  /**
   * Set when the query revalidates on a drawing. The list can be served with a freshness lifetime,
   * and a cached pre-draw body would hide the new numbers until that lifetime ends.
   */
  cache?: 'no-store';
  queryOptions?: {
    enabled?: boolean;
    staleTime?: (query: ResultsQueryObserver) => number;
    refetchInterval?: (query: ResultsQueryObserver) => number | false;
    refetchOnMount?: true;
  };
};

const MISSING_RESULT_POLL_MS = 5_000;
const MISSING_RESULT_POLL_MAX_MS = 60_000;
/** A drawing date within this of the scheduled instant is that drawing, not an older one. */
const DRAWING_INSTANT_TOLERANCE_MS = 60_000;

function getMissingResultPollMs(now: number, revalidateAt: number): number {
  const elapsedMs = Math.max(0, now - revalidateAt);
  const step = Math.min(10, Math.floor(elapsedMs / MISSING_RESULT_POLL_MS));
  return Math.min(MISSING_RESULT_POLL_MAX_MS, MISSING_RESULT_POLL_MS * 2 ** step);
}

function getFetchedAt(query: ResultsQueryObserver, now = Date.now()): number {
  return query.state.dataUpdatedAt || now;
}

function getDrawingTime(drawing: ResultsQueryDrawing): number {
  const time = Date.parse(drawing.drawingDate ?? '');
  return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY;
}

function hasDrawingNumbers(drawing: ResultsQueryDrawing | undefined): boolean {
  return (drawing?.numbers?.length ?? 0) > 0;
}

function isIndexedDrawingId(drawingId: string | undefined): drawingId is string {
  return typeof drawingId === 'string' && /_\d+$/.test(drawingId);
}

function getNewestDrawing(data: ResultsQueryDrawing[] | null | undefined): ResultsQueryDrawing | undefined {
  return (data ?? []).reduce<ResultsQueryDrawing | undefined>((newest, drawing) => (!newest || getDrawingTime(drawing) > getDrawingTime(newest) ? drawing : newest), undefined);
}

/**
 * The newest drawing, when its numbers have landed but its prize breakdown has not published yet.
 *
 * Only the newest one counts: an older drawing that never gets odds would otherwise keep every
 * open results page polling for as long as it stays in the list.
 */
function findDrawingAwaitingOdds(data: ResultsQueryDrawing[] | null | undefined): ResultsQueryDrawing | undefined {
  const latest = getNewestDrawing(data);
  if (!latest?.numbers?.length || hasUsablePrizeOdds(latest.odds)) return undefined;

  return latest;
}

/**
 * Whether the drawing this schedule is waiting on already has its numbers.
 *
 * An older row's numbers do not count. An indexed id is matched exactly. Otherwise a drawing date
 * within {@link DRAWING_INSTANT_TOLERANCE_MS} of the scheduled instant does. When the list has
 * neither, any row with numbers is the only signal the draw has landed.
 */
function dueDrawingHasNumbers(data: ResultsQueryDrawing[] | null | undefined, dueDrawingId: string | undefined, scheduledInstant: number): boolean {
  const rows = data ?? [];

  if (isIndexedDrawingId(dueDrawingId)) {
    return hasDrawingNumbers(rows.find(drawing => drawing.drawingId === dueDrawingId));
  }

  const matchedByDate = rows.find(drawing => {
    const time = Date.parse(drawing.drawingDate ?? '');
    return Number.isFinite(time) && Math.abs(time - scheduledInstant) <= DRAWING_INSTANT_TOLERANCE_MS;
  });
  if (matchedByDate) return hasDrawingNumbers(matchedByDate);

  const hasDatedRow = rows.some(drawing => Number.isFinite(Date.parse(drawing.drawingDate ?? '')));
  if (hasDatedRow) return false;

  return rows.some(drawing => hasDrawingNumbers(drawing));
}

function getRevalidateSchedule(
  query: ResultsQueryObserver,
  nextDrawingDate: number,
  offsetMs: number,
  nextDrawingId?: string,
  nextClosingDate?: number
): { revalidateAt: number; keepRetrying: boolean } {
  const awaitingOdds = findDrawingAwaitingOdds(query.state.data);
  if (awaitingOdds) {
    // Anchored on the draw rather than the last fetch, which moves on every poll, so the backoff still grows.
    const drawingTime = getDrawingTime(awaitingOdds);
    return { revalidateAt: Number.isFinite(drawingTime) ? drawingTime + offsetMs : getFetchedAt(query), keepRetrying: true };
  }

  if (hasSkippedUpcomingDrawing(query.state.data, nextDrawingId) && query.state.data) {
    const inferred = inferSkippedUpcomingDraw(query.state.data, {
      nextDrawingDate,
      nextClosingDate,
      nextDrawingId,
    });
    if (!inferred) return { revalidateAt: getFetchedAt(query), keepRetrying: true };

    const dueDrawingId = nextDrawingId ? getPreviousDrawingId(nextDrawingId) : undefined;
    return {
      revalidateAt: inferred.drawingDate + offsetMs,
      keepRetrying: !dueDrawingHasNumbers(query.state.data, dueDrawingId, inferred.drawingDate),
    };
  }

  // Next-draw can move on while this drawing is listed with an empty selection. Keep asking for
  // its numbers instead of waiting out the following draw.
  const newest = getNewestDrawing(query.state.data);
  const newestTime = newest ? getDrawingTime(newest) : Number.NEGATIVE_INFINITY;
  if (newest && Number.isFinite(newestTime) && newestTime < nextDrawingDate && !hasDrawingNumbers(newest)) {
    return { revalidateAt: newestTime + offsetMs, keepRetrying: true };
  }

  const dueDrawingId = isIndexedDrawingId(nextDrawingId) ? nextDrawingId : undefined;
  return {
    revalidateAt: nextDrawingDate + offsetMs,
    keepRetrying: !dueDrawingHasNumbers(query.state.data, dueDrawingId, nextDrawingDate),
  };
}

function getLatestResultsQueryOptions(limit?: number, nextDrawingDate?: number, offsetMs?: number, nextDrawingId?: string, nextClosingDate?: number): LatestResultsQueryOptions {
  if (limit == null) return { params: { limit: 0 }, queryOptions: { enabled: false } };

  const params = { limit };

  if (nextDrawingDate == null || offsetMs == null) return { params };

  return {
    params,
    cache: 'no-store',
    queryOptions: {
      staleTime: query => {
        const schedule = getRevalidateSchedule(query, nextDrawingDate, offsetMs, nextDrawingId, nextClosingDate);
        const fetchedAt = getFetchedAt(query);
        if (fetchedAt >= schedule.revalidateAt) {
          return schedule.keepRetrying ? 0 : Number.POSITIVE_INFINITY;
        }
        return schedule.revalidateAt - fetchedAt;
      },
      refetchInterval: query => {
        const schedule = getRevalidateSchedule(query, nextDrawingDate, offsetMs, nextDrawingId, nextClosingDate);

        if (!schedule.keepRetrying && getFetchedAt(query) >= schedule.revalidateAt) return false;

        const remainingMs = schedule.revalidateAt - Date.now();
        if (remainingMs > 0) return remainingMs;

        // A failed fetch must not end the backoff. The due drawing can still be missing.
        if (schedule.keepRetrying) return getMissingResultPollMs(Date.now(), schedule.revalidateAt);

        return false;
      },
      refetchOnMount: true,
    },
  };
}

export { getLatestResultsQueryOptions };
export type { LatestResultsQueryOptions, ResultsQueryObserver };
